import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import PrimaryButton from './PrimaryButton';
import ImpactStatement from './ImpactStatement';
import NetworkLogos from '@/components/ui/NetworkLogos';
import ImpactCard from './ImpactCard';
import ExampleProjects from './ExampleProjects';
import marketing1 from '@assets/images/create-profile.jpg'
import marketing2 from '@assets/images/plan-together.jpg'
import marketing3 from '@assets/images/connecting-classrooms.jpg'

// Static marketing copy for the microsite. Kept out of i18n. It may
// later be authored in WordPress and fetched, so it lives in this one component.

const containerPx = 'px-5 md:px-8 lg:px-16 lg:max-w-screen-xl lg:mx-auto';

ImageHolder.propTypes = {
  className: PropTypes.string,
  src: PropTypes.string
};

function ImageHolder(props) {
  const { className, src } = props;

  return (
    <div className={classNames('bg-cs-gray-100 rounded-cs-10 flex items-center justify-center object-cover overflow-hidden', className)}>
      <img src={src} alt="" aria-hidden="true" />
    </div>
  );
}

function AccentRule() {
  return <div className="h-0.5 w-32 bg-cs-orange-650" aria-hidden="true" />;
}

export default function MarketingContent() {

  return (
    <>
      {/* What is CommunityShare + image */}
      <section className="bg-white py-32">
        <div className={containerPx}>
          <div className="grid gap-8 md:gap-12 lg:gap-8 grid-cols-1 lg:grid-cols-2 items-start">
            <div className="flex flex-col gap-9">
              <div className="flex flex-col gap-6">
                <p className="text-sm font-semibold text-cs-orange-650 leading-normal">What is CommunityShare?</p>
                <h2 className="text-3xl font-semibold text-black leading-tight">
                  We connect classrooms with the people in their communities.
                </h2>
                <AccentRule />
                <p className="text-base text-cs-gray-500 leading-normal">
                  {'CommunityShare is a national nonprofit organization that connects students and educators with community members who have skills, stories, and life experience to share. We do this through a digital platform - think of it as a "human library" that connects classrooms and communities to bring real-world learning to life.'}
                </p>
              </div>
              <div>
                <p className="text-lg font-semibold text-cs-gray-900 leading-normal">{"Connection changes everything. Here's why it matters:"}</p>
                <p className="text-base text-cs-gray-500 leading-normal">
                  {"Many students feel disconnected from school when it doesn't seem relevant to their lives. But when students connect with people in their community, learning becomes more engaging, meaningful, and real."}
                </p>
              </div>
              <div>
                <p className="text-lg font-semibold text-cs-gray-900 leading-normal">You only need what you already have</p>
                <p className="text-base text-cs-gray-500 leading-normal">
                  {'When community partners like you share your experience, passions, and stories, students gain real insight and connection to the world beyond the classroom. And you gain something too — fresh perspective, new energy, and a meaningful way to contribute.'}
                </p>
              </div>
            </div>
            <ImageHolder src={marketing3} className="w-full h-80 lg:mt-12" />
          </div>

          <div className="mt-12">
            <ImpactStatement />
          </div>
          <div className="mt-12">
            <NetworkLogos />
          </div>
        </div>
      </section>

      {/* Real impact — kicker, heading, divider, two-col text, 4 cards */}
      <section className="bg-cs-yellow-100 py-32">
        <div className={containerPx}>
          <div className="flex flex-col gap-6 mb-8">
            <p className="text-sm font-semibold text-cs-orange-650 leading-normal">Why get involved?</p>
            <h2 className="font-semibold text-black leading-tight text-2xl md:text-3xl">
              Your story, skills, and passions can make a real difference
            </h2>
            <AccentRule />
          </div>

          <div className="grid gap-16 mb-16 grid-cols-1 lg:grid-cols-2">
            <div className="flex flex-col gap-3 text-base text-cs-gray-900 leading-normal">
              <p className="text-lg font-semibold">Your lived experience can make a real difference.</p>
              <p>{'The data and stories of students and educators show that community partners like you make a real difference for students by helping connect learning to the real world. Among students participating in CommunityShare:'}</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>98% of educators reported increased engagement and life skills.</li>
                <li>96% of educators say their students developed critical thinking, collaboration, and problem-solving skills.</li>
                <li>87% of educators say their students showed increased college and career readiness.</li>
              </ul>
            </div>
            <div className="flex flex-col gap-6 text-base text-cs-gray-900 leading-normal">
              <div className="flex flex-col gap-3">
                <p className="text-lg font-semibold">Flexible ways to participate in person or virtually:</p>
                <p>{'Some partners join for a single visit to the classroom. Others collaborate over weeks or months. Some connect virtually. You decide what works for your time, availability, and project goals.'}</p>
              </div>
              <div className="flex flex-col gap-3">
                <p className="text-lg font-semibold">Everyone is a learner. Everyone has something to share.</p>
                <p>{'This work is built on reciprocity. Students, educators, and partners all show up with ideas, skills, and perspectives to share. And most partners leave with more than they expected.'}</p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            <ImpactCard
              bg="bg-cs-yellow-500"
              textColor="text-cs-gray-900"
              title="Your presence matters"
              body={"One moment of encouragement or inspiration can open a student's eyes to new pathways and opportunities they never imagined. You make those moments possible."}
            />
            <ImpactCard
              bg="bg-green-800"
              textColor="text-white"
              title="Build community"
              body="Connect with people who care about making a difference. Build real relationships with students and educators, and stay rooted in your community."
            />
            <ImpactCard
              bg="bg-cs-orange-650"
              textColor="text-white"
              title="Learn something new"
              body="See your work and the world through a different lens. Students bring fresh questions, ideas, and perspectives that can challenge and inspire you."
            />
            <ImpactCard
              bg="bg-cs-navy-600"
              textColor="text-white"
              title="Gain experience"
              body="Use your skills in a new context, build your resume, grow as a leader, and gain experience that has real value."
            />
          </div>
        </div>
      </section>

      {/* Examples band */}
      <section className="bg-cs-navy-600 py-32">
        <div className={containerPx}>
          <h2 className="font-semibold text-white leading-tight mb-3 text-2xl md:text-3xl">
            Check out examples from the classroom and community
          </h2>
          <p className="text-base font-normal text-white leading-tight mb-8">
            {'See how students, educators, and community partners come together to explore questions, tackle real-world challenges, and learn from each other.'}
          </p>
          <ExampleProjects />
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white py-32">
        <div className={containerPx}>
          <div className="flex flex-col gap-6 mb-8">
            <p className="text-sm font-semibold text-cs-orange-650 leading-normal">Getting started</p>
            <h2 className="text-3xl font-semibold text-cs-gray-900 leading-tight">How it works</h2>
            <AccentRule />
          </div>

          {/* Row 1 — text + image (image first on mobile, text left on desktop) */}
          <div className="flex flex-col md:flex-row-reverse md:items-center gap-6 mb-16">
            <ImageHolder src={marketing1} className="w-full md:flex-1 h-80" />
            <div className="md:flex-1 flex flex-col gap-8">
              <p className="text-base text-cs-gray-500 leading-normal whitespace-pre-wrap">
                <span className="text-lg font-semibold text-cs-gray-900">{'1. Create your free partner profile\n'}</span>
                {"Join us by creating your partner profile and sharing a bit about your skills, interests, and life experience. You don't need a formal degree or teaching background. \n\n"}
                <span className="text-lg font-semibold text-cs-gray-900">{'2. Get matched with an opportunity\n'}</span>
                {'Browse projects in the platform and connect with educators. They can also reach out to you based on your skills and profile. Opportunities vary in topic, format, and time commitment. Choose what works for you.'}
              </p>
            </div>
          </div>

          {/* Row 2 — image + text + button (image left on desktop) */}
          <div className="flex flex-col md:flex-row md:items-start gap-5">
            <ImageHolder src={marketing2} className="w-full md:flex-1 h-80 md:h-96" />
            <div className="md:flex-1 flex flex-col gap-8">
              <p className="text-base text-cs-gray-500 leading-normal whitespace-pre-wrap">
                <span className="text-lg font-semibold text-cs-gray-900">{'3. Plan together\n'}</span>
                {'If you are interested, you and the educator can connect to shape an experience together. You can talk through goals, timing, format, and logistics so expectations are clear from the start.\n\n'}
                <span className="text-lg font-semibold text-cs-gray-900">{"4. We'll support you throughout the experience\n"}</span>
                {"You're not doing this alone, and you don't have to start big! The educator will co-create the experience with you, and we'll help support both of you as you go."}
              </p>
              <div className="w-full md:w-auto md:self-start">
                <PrimaryButton href='/signup'>Create your free account</PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
